import { ResolverTag } from '@ps/web-core-enums';
import type { TranslationTag } from '@ps/web-core-intl';
import { Size as ModalSize } from '@ps/web-core-store';
import { Translation, useNavigate, useResolver } from '@ps/web-shared-components';
import { isEmpty } from 'lodash';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useHistory, useLocation, useRouteMatch } from 'react-router';

import { styles } from '@swf/account';
import type { Props as ResolverIframeProps } from '@swf/components/Iframe/ResolverIframe';
import ResolverIframe from '@swf/components/Iframe/ResolverIframe';
import MitIdRedirect from '@swf/components/MitIdRedirect/MitIdRedirect';
import { ModalContext } from '@swf/components/StarsModal/Context';
import type { State as StarsModalProps } from '@swf/components/StarsModal/StarsModal';
import useSearchParams from '@swf/core/hooks/useSearchParams';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { Data, Params, ResolverProps } from '@swf/core/types/AccountResolver';
import {
  getAccountResolverProps,
  handleResolverPostMessage
} from '@swf/core/utils/accountResolver';
import { c2lHandler } from '@swf/core/utils/c2l';

import View from '../View/View';

import {
  TXTCLI_Bonuses,
  TXTCLI_Free_Bets,
  TXTCLI_Free_Spins,
  TXTCLI_Instant_Bonuses,
  TXTCLI_Prize_Games,
  TXTWCL_Sports_Bonus_Cash
} from './AccountResolver.messages.json';

const BONUS_TITLES: Record<string, TranslationTag> = {
  'free-spin-bonuses': TXTCLI_Free_Spins,
  'instant-bonuses': TXTCLI_Instant_Bonuses,
  'game-tickets': TXTCLI_Prize_Games,
  bonuses: TXTCLI_Bonuses,
  'sports-bonus-cash': TXTWCL_Sports_Bonus_Cash,
  'free-bets': TXTCLI_Free_Bets
};

export interface Props {
  onLoad?: ResolverIframeProps['onLoad'];
  hidden?: React.IframeHTMLAttributes<HTMLIFrameElement>['hidden'];
  title?: React.IframeHTMLAttributes<HTMLIFrameElement>['title'];
  overrides?: Partial<ResolverProps>;
}

const AccountResolver: React.FC<Props> = ({ onLoad, hidden, title, overrides }) => {
  const { token, signature } = useSelector(select.session.getLegacySession);
  const brand = useSelector(select.cms.getBrandId);
  const brandName = useSelector(select.cms.getBrandName);
  const country = useSelector(select.account.getCountry);
  const dataIntegrityCheckFailed = useSelector(select.account.dataIntegrityCheckFailed);
  const depositLimitPath = useSelector(select.account.getDepositLimitPath);
  const emailValidated = useSelector(select.account.isEmailValidated);
  const embedded = useSelector(select.device.isEmbedded);
  const embeddedRoute = useSelector(select.location.isEmbeddedRoute);
  const firstSession = useSelector(select.session.isFirstAuthenticatedSession);
  const isPlayMoney = useSelector(select.currency.isPlayMoney);
  const isRealMoneyOK = useSelector(select.account.isRealMoneyOK);
  const language = useSelector(select.locale.get);
  const license = useSelector(select.account.getLicense);
  const migrationRequired = useSelector(select.account.isMigrationRequired);
  const quickAccountCreationRequired = useSelector(select.account.isQuickAccountCreationRequired);
  const site = useSelector(select.cms.getSiteId);
  const encodedSignature = encodeURIComponent(signature);
  const encodedToken = encodeURIComponent(token as string);
  const migrationTarget = useSelector(select.account.getMigrationTarget);
  const hasClientSession = useSelector(select.session.hasClientSession);
  const features = useSelector(select.feature.get);
  const isReloader = useSelector(select.account.isReloader);
  const platform = useSelector(select.device.getPlatform);
  const assetUrl = useResolver(ResolverTag.ChestAssets);
  // eslint-disable-next-line
  const history = useHistory<any>(); // TODO: Provide history state types
  const { pathname } = useLocation();
  // eslint-disable-next-line
  const searchParams = useSearchParams<any>(); // TODO: Provide search params types
  const { params } = useRouteMatch<Params | undefined>() ?? {};
  const { setStyle: setContextStyle } = useContext(ModalContext);
  const [resolverProps, setResolverProps] = useState<ResolverIframeProps | null>(null);
  const [mitIdWc2Data, setMitIdWc2Data] = useState<object>({});
  const [style, setStyle] = useState<Partial<StarsModalProps> | null>(null);
  const navigate = useNavigate();
  const wrapInView = !!(pathname.startsWith('/account/my-stars/bonus') && params?.bonusType);
  const aliases = useSelector(select.cms.getAliases);

  const {
    modal: { close, replace: modalReplace },
    notification: { setPendingNotifications },
    auth: { logout },
    account: { fetch: refreshAccount }
  } = useDispatch<Dispatch>();

  useEffect(() => {
    const inferredParams = params ?? overrides?.params;

    if (!inferredParams) {
      setResolverProps(null);
      setStyle(null);
      return;
    }

    const props = getAccountResolverProps({
      assetUrl,
      brandName,
      country,
      emailValidated,
      firstSession,
      isRealMoneyOK,
      language,
      license,
      migrationRequired,
      migrationTarget,
      params: inferredParams,
      pathname,
      setPendingNotifications,
      site,
      styles,
      brand,
      close,
      embedded,
      embeddedRoute,
      isPlayMoney,
      searchParams,
      push: history.push,
      features,
      isReloader,
      platform,
      generatePath: () => (aliases ? generatePath(aliases.firstWalletHome ?? '', {}) : ''),
      getTokenAndSignature: () => ({ token: encodedToken, signature: encodedSignature }),
      navigate,
      ...overrides
    });
    setResolverProps(props?.resolverProps as ResolverIframeProps);
    setStyle(props?.styles as StarsModalProps);
  }, [
    aliases,
    assetUrl,
    brand,
    brandName,
    close,
    country,
    emailValidated,
    embedded,
    embeddedRoute,
    encodedSignature,
    encodedToken,
    features,
    firstSession,
    history,
    isPlayMoney,
    isRealMoneyOK,
    platform,
    isReloader,
    language,
    license,
    searchParams,
    migrationRequired,
    migrationTarget,
    navigate,
    params,
    pathname,
    setPendingNotifications,
    site,
    overrides
  ]);

  const handleMessage = useCallback(
    async ({ data }: Data) => {
      await handleResolverPostMessage({
        data,
        close,
        dataIntegrityCheckFailed,
        depositLimitPath,
        license,
        logout,
        modalReplace,
        push: history.push,
        quickAccountCreationRequired,
        refreshAccount,
        setMitIdWc2Data,
        c2lHandler: (c2l: string) => c2lHandler(c2l, history, navigate)
      });
    },
    [
      close,
      dataIntegrityCheckFailed,
      depositLimitPath,
      license,
      logout,
      modalReplace,
      history,
      quickAccountCreationRequired,
      refreshAccount,
      navigate
    ]
  );

  useEffect(() => {
    !hidden && window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage, hidden]);

  useEffect(() => {
    if (style) {
      setContextStyle(style);
    }

    return () => {
      setContextStyle({ size: ModalSize.EXTRA_LARGE });
    };
  }, [setContextStyle, style]);

  switch (true) {
    case !resolverProps || !hasClientSession:
      return null;
    case wrapInView:
      return (
        <View title={<Translation {...BONUS_TITLES[params?.bonusType as string]} />} nowrap>
          <ResolverIframe
            {...(resolverProps as ResolverIframeProps)}
            onLoad={onLoad}
            hidden={hidden}
          />
        </View>
      );
    case !isEmpty(mitIdWc2Data):
      return <MitIdRedirect returnModal="/account/cashier/rmf/" values={mitIdWc2Data} />;
    default:
      return (
        <ResolverIframe
          {...(resolverProps as ResolverIframeProps)}
          onLoad={onLoad}
          hidden={hidden}
          title={title}
        />
      );
  }
};

export default AccountResolver;
